import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  AlertTriangle, Activity, Clock, Users, 
  ArrowUp, ArrowDown, Calendar, FileText 
} from 'lucide-react';

const API_BASE_URL = 'https://ior.org/risk-register/web/api';

// Summary Card Component
const SummaryCard = ({ title, value, change, icon: Icon, description }) => {
  const isPositive = parseFloat(change) >= 0;
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <h3 className="text-2xl font-bold mt-2">{value}</h3>
        </div>
        <div className="p-3 bg-blue-50 rounded-lg">
          <Icon className="w-6 h-6 text-blue-600" />
        </div>
      </div>
      {change && (
        <div className="mt-4">
          <span className={`inline-flex items-center text-sm ${
            isPositive ? 'text-green-600' : 'text-red-600'
          }`}>
            {isPositive ? (
              <ArrowUp className="w-4 h-4 mr-1" />
            ) : (
              <ArrowDown className="w-4 h-4 mr-1" />
            )}
            {Math.abs(change)}%
          </span>
          <span className="text-sm text-gray-500 ml-2">vs last period</span>
        </div>
      )}
      {description && (
        <p className="mt-2 text-sm text-gray-600">{description}</p>
      )}
    </div>
  );
};

// Recent Activity Component
const RecentActivity = ({ activities }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h2 className="text-lg font-medium mb-4">Recent Activity</h2>
    <div className="space-y-4">
      {activities.map((activity, index) => (
        <div key={index} className="flex items-start space-x-3">
          <div className="flex-shrink-0">
            <activity.icon className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{activity?.title}</p>
            <p className="text-sm text-gray-500">{activity?.description}</p>
            <p className="text-xs text-gray-400 mt-1">{activity?.timestamp}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// Main Dashboard Component
const Dashboard = () => {
  const { user } = useAuth();
  const [metrics, setMetrics] = useState({
    totalRisks: { value: 0, change: 0 },
    highRisks: { value: 0, change: 0 },
    pendingApprovals: { value: 0, change: 0 },
    mitigationRate: { value: 0, change: 0 }
  });
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');

        const response = await fetch(`${API_BASE_URL}/dashboard.php`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();

        if (data.success) {
          setMetrics(data.metrics);
          setActivities(data.activities);
        } else {
          throw new Error(data.error || 'Failed to fetch dashboard data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold mb-2">Welcome back, {user?.full_name}</h1>
        <p className="text-gray-600">Here's what's happening with your risks today.</p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
          <div className="flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2" />
            {error}
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          title="Total Risks"
          value={metrics.totalRisks.value}
          change={metrics.totalRisks.change}
          icon={AlertTriangle}
          description="Active risks in the system"
        />
        <SummaryCard
          title="High Risk Items"
          value={metrics.highRisks.value}
          change={metrics.highRisks.change}
          icon={Activity}
          description="Risks requiring immediate attention"
        />
        <SummaryCard
          title="Pending Approvals"
          value={metrics.pendingApprovals.value}
          change={metrics.pendingApprovals.change}
          icon={Clock}
          description="Risks awaiting review"
        />
        <SummaryCard
          title="Mitigation Rate"
          value={`${metrics.mitigationRate?.value || 0}%`}
          change={metrics.mitigationRate?.change}
          icon={Activity}
          description="Successfully mitigated risks"
        />
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          { /*<RecentActivity activities={activities} /> */}
        </div>
        <div>
          {/* Upcoming Reviews or Important Notifications could go here */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-medium mb-4">Upcoming Reviews</h2>
            {/* Add upcoming reviews content */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
